import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import {
  defaultLocale,
  dictEn,
  dictFr,
  isLocale,
  supportedLocales,
  t as rawT,
  type DictKey,
  type Locale,
} from "@motorscan/i18n";

/**
 * Locale resolution order:
 *   1. ?lang=xx query param (highest, lets SEO surface link us deeply pre-logged-in)
 *   2. localStorage("motorscan.locale") — explicit user choice persists
 *   3. <html lang="…"> attribute (server-rendered hint, none for us currently)
 *   4. navigator.language
 *   5. defaultLocale
 *
 * On the server (SSR/initial render before hydration) we just use the default.
 * Hydration mismatches are avoided because we don't render any translated copy
 * outside of `useT()`, which always runs after mount.
 */
function detectInitialLocale(): Locale {
  if (typeof window === "undefined") return defaultLocale;
  try {
    const params = new URLSearchParams(window.location.search);
    const q = params.get("lang");
    if (q && isLocale(q)) return q;
  } catch {
    /* noop */
  }
  try {
    const stored = window.localStorage.getItem("motorscan.locale");
    if (stored && isLocale(stored)) return stored;
  } catch {
    /* noop */
  }
  const htmlLang = document.documentElement.lang;
  if (htmlLang && isLocale(htmlLang.slice(0, 2))) return htmlLang.slice(0, 2) as Locale;
  if (navigator.language) {
    const short = navigator.language.slice(0, 2);
    if (isLocale(short)) return short;
  }
  return defaultLocale;
}

interface I18nCtx {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: DictKey, vars?: Record<string, string | number>) => string;
  numberLocale: string;
}

const Ctx = createContext<I18nCtx | null>(null);

export function I18nProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [locale, setLocaleState] = useState<Locale>(() => detectInitialLocale());

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    try {
      window.localStorage.setItem("motorscan.locale", l);
    } catch {
      /* noop */
    }
    // Update URL ?lang= so deep links / refreshes preserve the choice.
    try {
      const u = new URL(window.location.href);
      if (l === defaultLocale) {
        u.searchParams.delete("lang");
      } else {
        u.searchParams.set("lang", l);
      }
      window.history.replaceState(null, "", u.toString());
    } catch {
      /* noop */
    }
  }, []);

  const t = useCallback(
    (key: DictKey, vars?: Record<string, string | number>) => rawT(key, locale, vars),
    [locale],
  );

  const value = useMemo<I18nCtx>(
    () => ({
      locale,
      setLocale,
      t,
      numberLocale: locale === "fr" ? "fr-FR" : "en-US",
    }),
    [locale, setLocale, t],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useT(): I18nCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("useT must be used within <I18nProvider>");
  return v;
}

export { dictFr, dictEn, supportedLocales, defaultLocale };
export type { Locale, DictKey };
